import { InteractWatchIcon } from '@/client/assets';
import { ApiTranslation } from '@/client/components/api-translation';
import { CurrencyView } from '@/client/components/currency-view';
import { FollowMarket } from '@/client/components/follow-market';
import { MarketDeadline } from '@/client/components/market-deadline';
import { OptionButton } from '@/client/components/option-button';
import { ShareMarket } from '@/client/components/share-market';
import { TextTooltip } from '@/client/components/text-tooltip';
import { UploadedImage } from '@/client/components/uploaded-image';
import useLocale from '@/client/hooks/use-locale';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { pickLocaleText } from '@/client/utils/i18n-content';
import { useEventDetail } from '@/libs/react-sdk/api/use-event-detail';
import { FollowStatus, type Market, MarketStatus } from '@/libs/sdk/api';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'wouter';

import { buildEventCardModel, distinctiveLabels, resolveEventCardHref } from './model';

type EventCardProps = {
  eventId: string;
  markets: Market[];
  className?: string;
};

export const EventCard = ({ eventId, markets, className }: EventCardProps) => {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [, navigate] = useLocation();
  const { data: eventDetail } = useEventDetail(api, { eventId }, { enabled: markets.length > 1 && !!eventId });

  const rawTitle = eventDetail?.title || markets[0]?.marketName || '';
  // Prefer the per-locale event title; keep the localized string as model.title so
  // the row-label prefix strip matches localized sub-market names too.
  const localizedTitle = pickLocaleText(eventDetail?.titleI18n, locale);
  const title = localizedTitle ?? rawTitle;
  const model = buildEventCardModel(markets, title);
  const href = resolveEventCardHref(markets, eventId);

  // The distinctive noun for each leg, computed across ALL of the event's legs
  // (not just the two shown) so the shared question stem/tail is detected. Keyed
  // by market address, using the same locale-preferred name the row will render.
  const rowNames = markets.map(m => pickLocaleText(m.nameI18n, locale) ?? m.marketName);
  const labels = distinctiveLabels(rowNames);
  const shortLabels = new Map(markets.map((m, i) => [m.marketAddress, labels[i]]));

  if (!model) return null;

  const leadMarket = model.leadMarket;

  return (
    <div
      className={cn(
        'mcard-neon mcard-surface group p-5 w-full flex flex-col',
        'rounded-2xl',
        'transition-all duration-200 ease-out',
        className
      )}
    >
      {/* Header - same as MarketCard. Real link so it's keyboard-reachable. */}
      <Link href={href} className="flex items-start gap-3 cursor-pointer">
        <div className="shrink-0 size-10 rounded-lg overflow-hidden bg-gradient-to-br from-brand-primary/20 to-brand-accent/20 flex items-center justify-center relative border border-border">
          <UploadedImage
            fileKey={eventDetail?.picUrl || leadMarket.marketPicUrl}
            alt=""
            className="size-full object-cover absolute inset-0"
          />
        </div>
        <TextTooltip
          tooltip={model.title}
          className="flex-1 font-medium text-sm text-foreground leading-snug line-clamp-2 hover:text-foreground/70 transition-colors"
        >
          {localizedTitle ? model.title : <ApiTranslation>{model.title}</ApiTranslation>}
        </TextTooltip>
      </Link>

      {/* Multi-option rows - same layout as old subMarkets */}
      <div className="mt-3 space-y-2">
        {model.displayRows.length > 0 ? (
          model.displayRows.map(({ market, yes, no, probability }) => {
            const localizedRow = pickLocaleText(market.nameI18n, locale);
            return (
            <div key={market.marketAddress} className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-foreground/85 truncate flex-1 text-xs">
                  {shortLabels.get(market.marketAddress) ?? localizedRow ?? market.marketName}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  <span
                    className="font-mono text-xs font-semibold"
                    style={{ color: 'var(--neon-yes)' }}
                  >
                    {probability}%
                  </span>
                <OptionButton
                  marketAddress={market.marketAddress}
                  marketDecimal={yes.decimal}
                  optionAddress={yes.tokenAddress}
                  optionName={yes.tokenName}
                  optionSide="yes"
                  optionDescription={yes.tokenDescription}
                  optionPrice={Number(yes.tokenPrice)}
                  optionDecimal={yes.decimal}
                  showDescription={false}
                  className="h-5 px-1.5 rounded text-[10px] font-medium"
                >
                  YES
                </OptionButton>
                <OptionButton
                  marketAddress={market.marketAddress}
                  marketDecimal={no.decimal}
                  optionAddress={no.tokenAddress}
                  optionName={no.tokenName}
                  optionSide="no"
                  optionDescription={no.tokenDescription}
                  optionPrice={Number(no.tokenPrice)}
                  optionDecimal={no.decimal}
                  showDescription={false}
                  className="h-5 px-1.5 rounded text-[10px] font-medium"
                >
                  NO
                </OptionButton>
                </div>
              </div>
            </div>
            );
          })
        ) : (
          <button
            className="w-full rounded-lg border border-border px-3 py-2 text-start text-sm text-muted-foreground transition-colors hover:text-foreground"
            onClick={() => navigate(href)}
          >
            {t('view-market', 'View market details')}
          </button>
        )}
        {model.displayRows.length > 0 && model.remainingCount > 0 && (
          <button
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => navigate(href)}
          >
            +{model.remainingCount} more
          </button>
        )}
      </div>

      {/* Footer - same as MarketCard */}
      <div className="mt-auto pt-3 flex items-center justify-between text-[11px] text-muted-foreground">
        <CurrencyView
          value={model.totalVolume}
          decimal={leadMarket.marketDecimal}
          baseTokenAddress={leadMarket.baseTokenAddress}
        >
          {t('vol')}
        </CurrencyView>
        <div className="flex items-center gap-2">
          {model.nearestDeadline && (
            <MarketDeadline
              deadline={model.nearestDeadline}
              status={model.status}
              className="text-[11px]"
            />
          )}
          <FollowMarket
            variant="ghost"
            iconClassName="p-0"
            className="size-3.5 hover:text-foreground transition-colors"
            watchIcon={<InteractWatchIcon className="size-3.5 fill-current" />}
            isFollowed={leadMarket.isFollowed == FollowStatus.yes}
            address={leadMarket.marketAddress}
          />
          <ShareMarket
            variant="ghost"
            className="size-3.5 hover:text-foreground transition-colors"
            iconClassName="p-0"
            marketName={leadMarket.marketName}
            nameI18n={leadMarket.nameI18n}
            marketAddress={leadMarket.marketAddress}
          />
        </div>
      </div>
    </div>
  );
};
